import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@ui/AppShell";
import { SetupWizard } from "@ui/SetupWizard";
import { SignIn } from "@ui/SignIn";
import { useAppStore, selectCurrentUser } from "@state/store";
import {
  Accounts,
  Dashboard,
  ImportExport,
  Journal,
  Reconcile,
  Settings,
  Statements,
  TrialBalance,
} from "@ui/pages";

/**
 * App root: loads the ledger from the repository once, then renders the branded
 * shell with the route table. Each page owns its own loading/empty/error state,
 * so the shell mounts immediately (ARCHITECTURE §3.4, §7).
 */
export function App() {
  const load = useAppStore((s) => s.load);
  const status = useAppStore((s) => s.status);
  const meta = useAppStore((s) => s.meta);
  const currentUser = useAppStore(selectCurrentUser);

  useEffect(() => {
    if (status === "idle") void load();
  }, [status, load]);

  // First-run go-live: once metadata has loaded, if setup was never completed
  // the wizard takes over the whole screen until the organization is created.
  // Gating on `meta` (not `status`) keeps the wizard mounted while it submits
  // and if that submit fails, so its own error state stays visible.
  if (meta && !meta.setupComplete) {
    return <SetupWizard />;
  }

  // Authentication gate (Identity mandate #1): once the org exists, the app is
  // only usable with a valid session. Setup signs the owner in automatically,
  // so this shows on a fresh browser, after sign-out, or when a session lapses.
  if (meta && meta.setupComplete && !currentUser) {
    return <SignIn />;
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/accounts" element={<Accounts />} />
        <Route path="/trial-balance" element={<TrialBalance />} />
        <Route path="/statements" element={<Statements />} />
        <Route path="/import-export" element={<ImportExport />} />
        <Route path="/reconcile" element={<Reconcile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppShell>
  );
}
